import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import SocialButtons from "@/components/SocialButtons";
import { api, setToken } from "@/lib/api";
import { Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BrandMark from "@/components/BrandMark";
import AuthLegalFooter from "@/components/legal/AuthLegalFooter";
import { track, EVENTS } from "@/lib/analytics";

export default function Signup() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const refCode = params.get("ref") || "";
  const paidPlan = params.get("plan");     // 'pro' | 'premium' — carried from landing pricing card
  const paidPeriod = params.get("period"); // 'monthly' | 'yearly'
  const paidCurrency = params.get("currency"); // 'USD' | 'CAD' | 'EUR' | …
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup", {
        name, email, password, referral_code: refCode || undefined,
      });
      setToken(data.token);
      setUser(data.user);
      track(EVENTS.SIGN_UP_COMPLETED, {
        has_referral: Boolean(refCode),
        intended_plan: paidPlan || "free",
        intended_period: paidPeriod || undefined,
      });
      // Email/password signups always require verification. Send the user to
      // the "check your email" screen; the pricing-intent (?plan=&period=…)
      // will resume once verified because localStorage still remembers it.
      if (data.verification_required || data.user?.email_verified === false) {
        const q = new URLSearchParams();
        if (paidPlan && ["pro", "premium"].includes(paidPlan)) q.set("plan", paidPlan);
        if (paidPeriod && ["monthly", "yearly"].includes(paidPeriod)) q.set("period", paidPeriod);
        if (paidCurrency && /^[A-Z]{3}$/i.test(paidCurrency)) q.set("currency", paidCurrency.toUpperCase());
        const qs = q.toString();
        toast.success("Almost there — verify your email to unlock Career OS.");
        nav(qs ? `/verify-email?${qs}` : "/verify-email");
        return;
      }
      toast.success(refCode ? "Welcome — your referrer earns when you upgrade" : "Welcome to Career OS");
      if (paidPlan && ["pro", "premium"].includes(paidPlan)) {
        const q = new URLSearchParams({ plan: paidPlan });
        if (paidPeriod && ["monthly", "yearly"].includes(paidPeriod)) q.set("period", paidPeriod);
        if (paidCurrency && /^[A-Z]{3}$/i.test(paidCurrency)) q.set("currency", paidCurrency.toUpperCase());
        nav(`/app/pricing?${q.toString()}`);
      } else {
        nav("/app");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dark min-h-screen flex flex-col bg-background text-foreground" data-testid="signup-page">
      <div className="flex flex-1 min-h-0">
      <div className="hidden md:flex flex-1 bg-card p-10 flex-col justify-between border-r border-border relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots opacity-30 pointer-events-none" />
        <Link to="/" className="flex items-center gap-2 relative">
          <BrandMark size={32} className="rounded-lg" />
          <span className="font-display font-bold tracking-tight text-lg">Career OS</span>
        </Link>
        <div className="relative">
          <div className="font-mono text-xs uppercase tracking-widest text-primary mb-4">Start free · 60 seconds</div>
          <div className="font-display font-black text-4xl tracking-tight leading-tight mb-4">
            Stop applying. Start engineering your career.
          </div>
          <div className="text-sm text-muted-foreground leading-relaxed max-w-md">
            Upload your resume. AI parses your profile, benchmarks your salary, and drafts a personalized 5-year roadmap in under a minute.
          </div>
        </div>
        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground/60">Career OS v2.0</div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 md:p-10">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="signup-form">
          <div>
            <div className="font-display font-black text-3xl tracking-tight">Create your account</div>
            <div className="text-sm text-muted-foreground mt-2">
              Already have one?{" "}
              <Link
                to={`/login${paidPlan && ["pro", "premium"].includes(paidPlan)
                  ? `?plan=${paidPlan}${paidPeriod && ["monthly", "yearly"].includes(paidPeriod) ? `&period=${paidPeriod}` : ""}${paidCurrency && /^[A-Z]{3}$/i.test(paidCurrency) ? `&currency=${paidCurrency.toUpperCase()}` : ""}`
                  : ""}`}
                className="text-primary hover:underline font-medium"
                data-testid="link-login"
              >
                Log in
              </Link>
            </div>
            {refCode && (
              <div
                className="mt-3 inline-flex items-center gap-2 text-xs bg-[hsl(var(--success))/0.12] border border-[hsl(var(--success))/0.3] text-[hsl(var(--success))] px-3 py-1.5 rounded-md"
                data-testid="referral-banner"
              >
                <Gift size={12} /> Referred by code <span className="font-mono">{refCode}</span>
              </div>
            )}
          </div>
          <div>
            <Label htmlFor="signup-name">Full name</Label>
            <Input
              id="signup-name" data-testid="signup-name" required value={name}
              onChange={(e) => setName(e.target.value)} placeholder="Alex Chen" className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="signup-email">Email</Label>
            <Input
              id="signup-email" data-testid="signup-email" required type="email" value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="signup-password">Password (6+ characters)</Label>
            <Input
              id="signup-password" data-testid="signup-password" required type="password" value={password}
              onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5"
            />
          </div>
          <Button
            data-testid="signup-submit" type="submit" disabled={loading}
            className="w-full h-11 text-base"
          >
            {loading ? "Creating…" : "Create account"}
          </Button>
          <SocialButtons testIdPrefix="signup-" />
        </form>
        <p className="mt-6 text-[11px] text-muted-foreground text-center leading-relaxed">
          By creating an account, you agree to our{" "}
          <Link to="/terms" className="underline hover:text-foreground">Terms</Link> and{" "}
          <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>.
        </p>
      </div>
      </div>
      <AuthLegalFooter />
    </div>
  );
}
